import type { JSX } from 'solid-js'

export type IconName =
  | 'select'
  | 'hand'
  | 'rect'
  | 'ellipse'
  | 'line'
  | 'pen'
  | 'text'
  | 'undo'
  | 'redo'
  | 'trash'
  | 'front'
  | 'back'
  | 'eye'
  | 'eyeOff'
  | 'lock'
  | 'unlock'
  | 'plus'
  | 'folder'
  | 'download'
  | 'image'

// Thunks, not elements: a Solid element is a single DOM node, so sharing one
// across icons moves it into whichever icon rendered last.
const paths: Record<IconName, () => JSX.Element> = {
  select: () => <path d="M5 3l14 7.5-6.2 1.4L9.8 19z" fill="currentColor" />,
  hand: () => (
    <path d="M7 11V5.8a1.6 1.6 0 013.2 0V10m0-.6V4.6a1.6 1.6 0 013.2 0V10m0-.8V5.6a1.6 1.6 0 013.2 0V13c0 4-2.6 7-6.4 7-3 0-4.6-1.4-5.8-4L7.2 13c-.5-1.1.6-2 1.5-1.4L10 12.4" />
  ),
  rect: () => <rect x="4" y="5.5" width="16" height="13" rx="1.5" />,
  ellipse: () => <ellipse cx="12" cy="12" rx="8" ry="6.5" />,
  line: () => <path d="M4.5 19.5L19.5 4.5" />,
  pen: () => <path d="M3.5 16c2.5-6.5 4.5-9 6-9 2 0 1 5 2.5 5 2 0 3-8 5-8 1.6 0 1.2 4.5 3.5 4.5" />,
  text: () => <path d="M5 5.5h14M12 5.5V19M9 19h6" />,
  undo: () => <path d="M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 010 11H11" />,
  redo: () => <path d="M15 14l5-5-5-5M20 9H9.5a5.5 5.5 0 000 11H13" />,
  trash: () => <path d="M4 7h16M9 7V4.5h6V7M6 7l1 13h10l1-13M10 11v5M14 11v5" />,
  front: () => <path d="M12 3v12M8 7l4-4 4 4M4 19h16" />,
  back: () => <path d="M12 21V9M8 17l4 4 4-4M4 5h16" />,
  eye: () => <path d="M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12zM12 9.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" />,
  eyeOff: () => <path d="M4 4l16 16M9.5 9.7A2.5 2.5 0 0012 14.5c.6 0 1.2-.2 1.7-.6M6 6.9C3.9 8.4 2.5 12 2.5 12S6 17.5 12 17.5c1.6 0 3-.4 4.2-1M18.6 15c1.8-1.4 2.9-3 2.9-3S18 6.5 12 6.5c-.5 0-1 0-1.4.1" />,
  lock: () => <path d="M6 11h12v9H6zM8.5 11V7.5a3.5 3.5 0 017 0V11" />,
  unlock: () => <path d="M6 11h12v9H6zM8.5 11V7.5a3.5 3.5 0 016.9-1" />,
  plus: () => <path d="M12 5v14M5 12h14" />,
  folder: () => <path d="M3.5 6.5h5l2 2.5h10v9.5h-17z" />,
  download: () => <path d="M12 4v11M7.5 10.5L12 15l4.5-4.5M4.5 19.5h15" />,
  image: () => <path d="M4 5.5h16v13H4zM4 15l4.5-4 3.5 3 3-2.5L20 15M15 9.5a1 1 0 100-2 1 1 0 000 2z" />,
}

export function Icon(props: { name: IconName; size?: number }) {
  return (
    <svg
      class="icon"
      width={props.size ?? 20}
      height={props.size ?? 20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {paths[props.name]()}
    </svg>
  )
}
